import { useRouter } from 'next/router';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import useI18n from '@i18n';

import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import { IGetOffersDataFormatted, IGetTrafficSourcesDataFormatted } from '@api/types';

import AddPlacementForm from './AddPlacementForm';
import { IPlacementAddStepProps } from '../interfaces';

const { useOffers, useTrafficSources, useTrafficSourcesCategories } = apiHooks;

const AddPlacementStep: React.FC<IPlacementAddStepProps> = ({
    afterReg,
    createInProgress,
    onSubmit,
    onNext,
    isLoading,
}) => {
    const { t, locale } = useI18n('Placements');
    const router = useRouter();
    const { pathname } = router;

    // Список офферов
    const { data: offers, isLoading: offersIsLoading } = useOffers({
        params: {
            limit: 1000,
            lang: locale,
            viewRules: 'role_user',
            fields: 'title,link_match',
            order: 'webmasterPriority',
        },
        dependencies: [locale, pathname],
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    // Список источников трафика
    const { data: trafficSources, isLoading: TrafficSorcesIsLoading } = useTrafficSources<
        IGetTrafficSourcesDataFormatted[]
    >({
        dependencies: [locale],
    });

    // Список категорий источников трафика
    const {
        data: trafficCategories,
        isLoading: trafficCategoriesIsLoading,
    } = useTrafficSourcesCategories<IGetTrafficSourcesDataFormatted[]>({
        dependencies: [locale],
        params: {
            hasTrafficSources: true,
        },
    });

    return (
        <>
            <Skeleton loading={isLoading}>
                <h4 className="mb-2 font-medium text-blueGray-600 text-[18px] xs:text-[24px]">
                    {afterReg
                        ? t('New traffic source')
                        : t('Welcome! Lets connect the platform to get started')}
                </h4>

                {!afterReg && (
                    <p className="font-normal text-blueGray-500 text-[14px]">
                        {t(
                            'It will take no more than 3 minutes, and after successful verification you will be able to work with the best offers',
                        )}
                    </p>
                )}
                <ul className="pl-5 list-disc">
                    <li className="font-normal text-blueGray-500 text-[14px]">
                        {t(
                            'Select the type of traffic source where you plan to publish the advertisement',
                        )}
                    </li>
                    <li className="font-normal text-blueGray-500 text-[14px]">
                        {t(
                            'Indicate the link to your traffic source (from where you plan to direct users to the offer pages)',
                        )}
                    </li>
                    {afterReg && (
                        <li className="font-normal text-blueGray-500 text-[14px]">
                            {t('Select the offer that you plan to advertise')}
                        </li>
                    )}
                </ul>
            </Skeleton>

            <div className="mt-8 mb-36 md:max-w-[800px]">
                <AddPlacementForm
                    offers={offers}
                    isLoading={
                        offersIsLoading || TrafficSorcesIsLoading || trafficCategoriesIsLoading
                    }
                    createInProgress={createInProgress}
                    trafficSources={trafficSources}
                    trafficCategories={trafficCategories}
                    onSubmit={onSubmit}
                    onNext={onNext}
                    afterReg={afterReg}
                />
            </div>
        </>
    );
};

export default AddPlacementStep;
